import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAlert } from 'react-alert';

import { listDiscounts, deleteDiscount } from '../../../actions/discountActions';
import Loader from '../../Loader';

const ListDiscounts = () => {
  const dispatch = useDispatch();
  const alert = useAlert();

  const { discounts, loading, error } = useSelector((state) => state.discountsList);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    dispatch(listDiscounts({ includeArchived: showArchived }));
  }, [dispatch, showArchived]);

  useEffect(() => {
    if (error) alert.error(error);
  }, [error, alert]);

  const fmtDate = (d) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleDateString();
    } catch (e) {
      return d;
    }
  };

  const copyToClipboard = (text, message = 'Copied to clipboard') => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      alert.success(message);
    } catch (e) {
      alert.error('Failed to copy');
    }
  };

  const referralLinks = (code) => {
    if (!code) return { register: '', plans: '' };
    const appOrigin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
    const landingOrigin = process.env.REACT_APP_LANDING_BASE_URL || appOrigin;
    return {
      register: `${landingOrigin}/landing/register?ref=${encodeURIComponent(code)}`,
      plans: `${appOrigin}/subscription-plans?code=${encodeURIComponent(code)}`,
    };
  };

  const onDelete = (d) => {
    if (!d || !d._id) return;
    const ok = window.confirm(`Archive discount ${d.code}? This will disable it immediately.`);
    if (!ok) return;
    dispatch(deleteDiscount(d._id, { includeArchived: showArchived }));
  };

  return (
    <Fragment>
      <div className="content-wrapper">
        <section className="content-header">
          <h1>
            Manage <small>Discounts</small>
          </h1>
          <ol className="breadcrumb">
            <li>
              <Link to="/admin">
                <i className="fa fa-dashboard"></i> Dashboard
              </Link>
            </li>
            <li className="active">Discounts</li>
          </ol>
        </section>

        <section className="content">
          <div className="row">
            <div className="col-xs-12">
              <div className="box">
                <div className="box-header">
                  <h3 className="box-title">Discounts</h3>
                  <div className="box-tools pull-right" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <label style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={showArchived}
                        onChange={(e) => setShowArchived(e.target.checked)}
                      />
                      <span>Show archived</span>
                    </label>
                    <Link to="/admin/discounts/add" className="btn btn-success btn-sm">
                      + Add Discount
                    </Link>
                  </div>
                </div>
                <div className="box-body table-responsive no-padding">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Value</th>
                        <th>Valid From</th>
                        <th>Valid Until</th>
                        <th>Usage</th>
                        <th>Referral Links</th>
                        <th>Status</th>
                        <th>Archived</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="12" style={{ textAlign: 'center' }}>
                            <Loader type="Puff" color="#00BFFF" height={50} width={50} />
                          </td>
                        </tr>
                      ) : discounts && discounts.length ? (
                        discounts.map((d) => (
                          <tr key={d._id}>
                            <td>{d.code}</td>
                            <td>{d.name}</td>
                            <td>{d.type}</td>
                            <td>{d.value}</td>
                            <td>{fmtDate(d.validFrom)}</td>
                            <td>{fmtDate(d.validUntil)}</td>
                            <td>
                              {d.usageLimit == null ? (
                                <span className="badge bg-blue">Unlimited</span>
                              ) : (
                                <span className="badge bg-light-blue">{d.usedCount || 0}/{d.usageLimit}</span>
                              )}
                            </td>
                            <td>
                              {d.code ? (
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                  <button
                                    type="button"
                                    className="btn btn-default btn-xs"
                                    onClick={() => copyToClipboard(referralLinks(d.code).register, 'Registration link copied')}
                                    title="Copy Registration Referral Link"
                                  >
                                    Copy Register
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-default btn-xs"
                                    onClick={() => copyToClipboard(referralLinks(d.code).plans, 'Plans link copied')}
                                    title="Copy Plans Link with Code"
                                  >
                                    Copy Plans
                                  </button>
                                </div>
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                            <td>
                              {d.status ? (
                                <span className="badge bg-green">Active</span>
                              ) : (
                                <span className="badge bg-yellow">Inactive</span>
                              )}
                            </td>
                            <td>
                              {d.archived ? (
                                <span className="label label-default">Archived</span>
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <Link to={`/admin/discounts/edit/${d._id}`} className="btn btn-primary btn-xs">Edit</Link>
                                <button type="button" className="btn btn-danger btn-xs" onClick={() => onDelete(d)} disabled={!!d.archived}>
                                  Archive
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="12" style={{ textAlign: 'center' }}>
                            No records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Fragment>
  );
};

export default ListDiscounts;
